import { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Alert,
  Modal,
  Textarea,
  Badge,
  Card,
  Grid,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconFileText,
  IconUser,
  IconCalendar,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  usePendingReportApprovals,
  useCheckReport,
  useRejectCheck,
  useApproveReport,
  useRejectApproval,
  useApproveByBidCommittee,
  useRejectByBidCommittee,
} from '../hooks/useEvaluationReports';
import { useAuth } from '../../../auth/AuthContext';
import dayjs from 'dayjs';

export function EvaluationReportApprovals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: pendingApprovals, isLoading, error } = usePendingReportApprovals();
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [approvalModal, setApprovalModal] = useState(false);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [comments, setComments] = useState('');
  const [actionType, setActionType] = useState<'check' | 'approve' | 'bid-committee'>('check');

  const checkReport = useCheckReport();
  const rejectCheck = useRejectCheck();
  const approveReport = useApproveReport();
  const rejectApproval = useRejectApproval();
  const approveByBidCommittee = useApproveByBidCommittee();
  const rejectByBidCommittee = useRejectByBidCommittee();

  const handleApprove = (report: any) => {
    setSelectedReport(report);
    setActionType(report.approvalType === 'CHECK' ? 'check' : report.approvalType === 'APPROVE' ? 'approve' : 'bid-committee');
    setApprovalModal(true);
  };

  const handleReject = (report: any) => {
    setSelectedReport(report);
    setActionType(report.approvalType === 'CHECK' ? 'check' : report.approvalType === 'APPROVE' ? 'approve' : 'bid-committee');
    setRejectionModal(true);
  };

  const submitApprove = () => {
    if (!selectedReport) return;

    const mutation = 
      actionType === 'check' 
        ? checkReport 
        : actionType === 'approve' 
        ? approveReport 
        : approveByBidCommittee;

    mutation.mutate(
      { reportId: selectedReport.id, data: { comments: comments.trim() || undefined } },
      {
        onSuccess: () => {
          setApprovalModal(false);
          setSelectedReport(null);
          setComments('');
        },
      }
    );
  };

  const submitReject = () => {
    if (!selectedReport || !comments.trim()) return;

    const mutation = 
      actionType === 'check' 
        ? rejectCheck 
        : actionType === 'approve' 
        ? rejectApproval 
        : rejectByBidCommittee;

    mutation.mutate(
      { reportId: selectedReport.id, data: { comments: comments.trim() } },
      {
        onSuccess: () => {
          setRejectionModal(false);
          setSelectedReport(null);
          setComments('');
        },
      }
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING_CHECK':
        return <Badge color="yellow">Pending Check</Badge>;
      case 'PENDING_APPROVAL':
        return <Badge color="orange">Pending Approval</Badge>;
      case 'PENDING_BID_COMMITTEE':
        return <Badge color="blue">Pending Bid Committee</Badge>;
      case 'APPROVED':
        return <Badge color="green">Approved</Badge>;
      case 'REJECTED':
        return <Badge color="red">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getApprovalTypeLabel = (type: string) => {
    switch (type) {
      case 'CHECK':
        return 'Check Required';
      case 'APPROVE':
        return 'Approval Required';
      case 'BID_COMMITTEE':
        return 'Bid Committee Approval';
      default:
        return 'Action Required';
    }
  };

  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Alert color="blue" variant="light">
          <Text>Loading pending approvals...</Text>
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" variant="light">
          <Text>Failed to load pending approvals: {error.message}</Text>
        </Alert>
      </Container>
    );
  }

  if (!pendingApprovals || pendingApprovals.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Paper shadow="sm" p="xl">
          <Group mb="xl">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/tenders')}
            >
              Back to Tenders
            </Button>
          </Group>

          <Title order={2} mb="xl">
            Evaluation Report Approvals
          </Title>

          <Alert color="green" variant="light">
            <Text>No pending approvals found.</Text>
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/tenders')}
          >
            Back to Tenders
          </Button>
        </Group>

        <Title order={2} mb="xl">
          Evaluation Report Approvals
        </Title>

        <Grid>
          {pendingApprovals.map((report: any) => (
            <Grid.Col key={report.id} span={{ base: 12, md: 6, lg: 4 }}>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="sm">
                  <Group justify="space-between">
                    {getStatusBadge(report.status)}
                    <Badge color="blue" variant="light">
                      {getApprovalTypeLabel(report.approvalType)}
                    </Badge>
                  </Group>

                  <div>
                    <Text fw={500} size="lg" mb="xs">
                      {report.tender?.description || 'Tender'}
                    </Text>
                    <Text size="sm" color="dimmed" lineClamp={2}>
                      {report.tender?.referenceNo || 'N/A'}
                    </Text>
                  </div>

                  <Group gap="xs" align="center">
                    <IconUser size={14} />
                    <Text size="xs" color="dimmed">
                      Generated by: {report.generatedBy?.fullName || 'Unknown'}
                    </Text>
                  </Group>

                  <Group gap="xs" align="center">
                    <IconCalendar size={14} />
                    <Text size="xs" color="dimmed">
                      {dayjs(report.generatedAt).format('MMM DD, YYYY')}
                    </Text>
                  </Group>

                  <Group gap="xs" mt="md">
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      leftSection={<IconFileText size={14} />}
                      onClick={() => navigate(`/tenders/${report.tenderId}/evaluation-report`)}
                    >
                      View Report
                    </Button>
                  </Group>

                  <Group gap="xs" mt="sm">
                    <Button
                      size="xs"
                      color="green"
                      leftSection={<IconCheck size={14} />}
                      onClick={() => handleApprove(report)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      leftSection={<IconX size={14} />}
                      onClick={() => handleReject(report)}
                    >
                      Reject
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>

        {/* Approval Modal */}
        <Modal
          opened={approvalModal}
          onClose={() => {
            setApprovalModal(false);
            setComments('');
          }}
          title={`Approve ${actionType === 'check' ? 'Check' : actionType === 'approve' ? 'Report' : 'Bid Committee Approval'}`}
        >
          <Stack>
            <Textarea
              label="Comments (Optional)"
              placeholder="Add any comments..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              minRows={3}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setApprovalModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={submitApprove}
                loading={
                  actionType === 'check'
                    ? checkReport.isPending
                    : actionType === 'approve'
                    ? approveReport.isPending
                    : approveByBidCommittee.isPending
                }
              >
                Approve
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Rejection Modal */}
        <Modal
          opened={rejectionModal}
          onClose={() => {
            setRejectionModal(false);
            setComments('');
          }}
          title="Reject Report"
        >
          <Stack>
            <Alert color="red" mb="md">
              Please provide a reason for rejection. The report will be sent back to the generator.
            </Alert>
            <Textarea
              label="Rejection Comments (Required)"
              placeholder="Explain why you are rejecting this report..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              minRows={3}
              required
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setRejectionModal(false)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={submitReject}
                loading={
                  actionType === 'check'
                    ? rejectCheck.isPending
                    : actionType === 'approve'
                    ? rejectApproval.isPending
                    : rejectByBidCommittee.isPending
                }
                disabled={!comments.trim()}
              >
                Reject
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Paper>
    </Container>
  );
}

